import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

interface ArchLayer {
  label: string;
  color: string;
  icons: string[];
  delay: number;
  hasCheck?: boolean;
}

const LAYERS: ArchLayer[] = [
  {
    label: "Raw Knowledge",
    color: COLORS.layer0,
    icons: ["📄 PDF", "💻 Code", "📁 Files"],
    delay: 40,
  },
  {
    label: "Smart Ingestion",
    color: COLORS.layer1,
    icons: ["🔍 Auto-detect", "👤 Entities", "💬 Sentiment"],
    delay: 70,
  },
  {
    label: "Generation Modes",
    color: COLORS.layer2,
    icons: ["✉️ Email", "📊 Research", "⚔️ Battle Card"],
    delay: 100,
    hasCheck: true,
  },
  {
    label: "User-Defined Tools",
    color: COLORS.layer3,
    icons: ["🛠️ Describe", "⚙️ Create", "📈 Dashboard"],
    delay: 130,
  },
  {
    label: "Autonomous Chains",
    color: COLORS.layer4,
    icons: ["🎯 Intent", "🔄 Decompose", "⚡ Execute"],
    delay: 160,
  },
];

const LAYER_WIDTH = 1400;
const LAYER_HEIGHT = 110;
const LAYER_GAP = 130;
const STACK_TOP = 140;

export const Scene05Architecture: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Gentle rotateY oscillation
  const rotateY = Math.sin(frame * 0.01) * 3;

  // Background grid pulse
  const gridOpacity = 0.04 + Math.sin(frame * 0.02) * 0.02;

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* Animated SVG grid background */}
      <svg
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: gridOpacity }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid05" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke={COLORS.neonBlue} strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid05)" />
      </svg>

      {/* Radial glow at center */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 900,
          height: 700,
          transform: "translate(-50%, -50%)",
          background: `radial-gradient(ellipse, ${COLORS.neonPurple}10 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonPurple, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonBlue, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonGreen, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 30,
          left: "50%",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
          transform: `translateX(-50%) translateY(${interpolate(titleAppear, [0, 1], [-20, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })}px)`,
        }}
      >
        <div
          style={{
            fontSize: 48,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            background: "linear-gradient(90deg, #FF2D95, #A855F7, #00D4FF, #00FF88, #FFD60A)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: "-1px",
          }}
        >
          The Five Layers
        </div>
        <div
          style={{
            fontSize: 16,
            fontFamily: FONTS.body,
            color: COLORS.textMuted,
            marginTop: 6,
          }}
        >
          EAKC Knowledge Architecture
        </div>
      </div>

      {/* 3D Stack Container */}
      <div
        style={{
          position: "absolute",
          top: STACK_TOP,
          left: "50%",
          transform: `translateX(-50%) perspective(1200px) rotateX(15deg) rotateY(${rotateY}deg) translateZ(${Math.sin(frame * 0.01) * 3}px)`,
          transformStyle: "preserve-3d",
          width: LAYER_WIDTH,
        }}
      >
        {LAYERS.map((layer, i) => {
          const layerAppear = spring({
            frame: frame - layer.delay,
            fps,
            config: SPRING.bouncy,
          });

          const opacity = interpolate(layerAppear, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const translateY = interpolate(layerAppear, [0, 1], [60, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const scale = interpolate(layerAppear, [0, 1], [0.85, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });

          // Pulse highlight: sequential glow after frame 250
          const pulseStart = 250 + i * 15;
          const pulseEnd = pulseStart + 15;
          const pulseRaw = interpolate(frame, [pulseStart, pulseEnd, pulseEnd + 30], [0, 1, 0.3], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });

          const glowIntensity = 0.15 + pulseRaw * 0.6;
          const shadowSpread = 20 + pulseRaw * 40;

          const topPos = 70 + i * LAYER_GAP;

          return (
            <React.Fragment key={i}>
              {/* Layer card */}
              <div
                style={{
                  position: "absolute",
                  top: topPos,
                  left: 0,
                  width: LAYER_WIDTH,
                  height: LAYER_HEIGHT,
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${layer.color}22 0%, ${layer.color}0A 50%, ${layer.color}14 100%)`,
                  border: `1.5px solid ${layer.color}${Math.round(glowIntensity * 255).toString(16).padStart(2, "0")}`,
                  opacity,
                  transform: `translateY(${translateY}px) scale(${scale})`,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 40px",
                  boxShadow: `0 0 ${shadowSpread}px ${layer.color}40, 0 0 ${shadowSpread * 2}px ${layer.color}15, inset 0 1px 0 ${layer.color}20`,
                  backdropFilter: "blur(4px)",
                } as React.CSSProperties}
              >
                {/* Layer index */}
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: `${layer.color}20`,
                    border: `2px solid ${layer.color}60`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: FONTS.mono,
                    fontSize: 16,
                    fontWeight: 700,
                    color: layer.color,
                    flexShrink: 0,
                    marginRight: 20,
                    boxShadow: `0 0 12px ${layer.color}40`,
                  }}
                >
                  {i}
                </div>

                {/* Label */}
                <div style={{ minWidth: 260 }}>
                  <div
                    style={{
                      fontSize: 22,
                      fontFamily: FONTS.heading,
                      fontWeight: 700,
                      color: layer.color,
                      textShadow: `0 0 20px ${layer.color}80`,
                    }}
                  >
                    {layer.label}
                    {layer.hasCheck && (
                      <span
                        style={{
                          marginLeft: 12,
                          fontSize: 18,
                          color: COLORS.neonGreen,
                          textShadow: `0 0 10px ${COLORS.neonGreen}`,
                        }}
                      >
                        ✅
                      </span>
                    )}
                  </div>
                </div>

                {/* Icons row */}
                <div
                  style={{
                    display: "flex",
                    gap: 20,
                    marginLeft: "auto",
                  }}
                >
                  {layer.icons.map((icon, j) => {
                    const iconAppear = spring({
                      frame: frame - layer.delay - 15 - j * 8,
                      fps,
                      config: SPRING.snappy,
                    });
                    return (
                      <div
                        key={j}
                        style={{
                          padding: "8px 18px",
                          borderRadius: 10,
                          background: `${layer.color}15`,
                          border: `1px solid ${layer.color}35`,
                          fontFamily: FONTS.body,
                          fontSize: 14,
                          color: COLORS.textSecondary,
                          opacity: interpolate(iconAppear, [0, 1], [0, 1], {
                            extrapolateLeft: "clamp",
                            extrapolateRight: "clamp",
                          }),
                          transform: `scale(${interpolate(iconAppear, [0, 1], [0.6, 1], {
                            extrapolateLeft: "clamp",
                            extrapolateRight: "clamp",
                          })})`,
                          whiteSpace: "nowrap",
                          boxShadow: `0 0 8px ${layer.color}20`,
                        }}
                      >
                        {icon}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Vertical dashed flow arrow between layers */}
              {i < LAYERS.length - 1 && (
                <svg
                  style={{
                    position: "absolute",
                    top: topPos + LAYER_HEIGHT,
                    left: LAYER_WIDTH / 2 - 10,
                    width: 20,
                    height: LAYER_GAP - LAYER_HEIGHT,
                    pointerEvents: "none",
                    opacity: interpolate(layerAppear, [0, 1], [0, 0.8], {
                      extrapolateLeft: "clamp",
                      extrapolateRight: "clamp",
                    }),
                  }}
                >
                  <line
                    x1="10"
                    y1="0"
                    x2="10"
                    y2={LAYER_GAP - LAYER_HEIGHT}
                    stroke={layer.color}
                    strokeWidth="2"
                    strokeDasharray="6 4"
                    strokeDashoffset={-frame * 2}
                  />
                  {/* Arrow head */}
                  <polygon
                    points={`5,${LAYER_GAP - LAYER_HEIGHT - 8} 15,${LAYER_GAP - LAYER_HEIGHT - 8} 10,${LAYER_GAP - LAYER_HEIGHT}`}
                    fill={layer.color}
                    opacity={0.8}
                  />
                </svg>
              )}
            </React.Fragment>
          );
        })}

        {/* Glowing particles flowing upward */}
        {[...Array(6)].map((_, pi) => {
          const particlePhase = (frame * 1.5 + pi * 60) % 360;
          const particleY = STACK_TOP + 70 + (particlePhase / 360) * (LAYERS.length * LAYER_GAP);
          const particleX = 100 + pi * 230 + Math.sin((frame + pi * 40) * 0.05) * 20;
          const particleColor = LAYERS[Math.floor(pi % LAYERS.length)].color;
          return (
            <div
              key={`particle-${pi}`}
              style={{
                position: "absolute",
                left: particleX,
                top: particleY,
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: particleColor,
                boxShadow: `0 0 12px ${particleColor}, 0 0 24px ${particleColor}60`,
                opacity: 0.7,
                pointerEvents: "none",
              }}
            />
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
